import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Cloud, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CloudText, Reveal, AltitudeMarker } from '@/components/CloudText';
import { NEEDS } from '@/data/content';
import { useExperience } from '@/store/experience';
import { ReferenceUploader } from './ReferenceUploader';
import { BudgetSelector } from './BudgetSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', contact: '', business: '', need: '', description: '', budget: '', deadline: null };

export function ProjectRequest() {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const setPhase = useExperience((s) => s.setPhase);
  const set = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Tell us your name';
    if (!form.contact.trim()) e.contact = 'How do we reach you?';
    if (!form.need) e.need = 'Pick what you need';
    if (form.description.trim().length < 10) e.description = 'A few more words about your idea';
    if (!form.budget) e.budget = 'Choose a budget range';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error('The cloud needs a few more details.'); return; }
    setSending(true);
    setFiles((fs) => fs.map((f) => ({ ...f, status: 'uploading' })));
    try {
      const fd = new FormData();
      Object.entries({ ...form, deadline: form.deadline ? format(form.deadline, 'yyyy-MM-dd') : '' }).forEach(([k, v]) => fd.append(k, v ?? ''));
      files.forEach((f) => fd.append('files', f.file, f.file.name));
      const { data } = await axios.post(`${API}/submissions`, fd);
      setFiles((fs) => fs.map((f) => ({ ...f, status: 'sent' })));
      setDone(true);
      setPhase('PROJECT_SUBMITTED');
      if (!data.emailed) toast.message('Saved to the cloud. Email notification is delayed — we still have your request.');
    } catch (err) {
      setFiles((fs) => fs.map((f) => ({ ...f, status: 'ready' })));
      toast.error(err?.response?.data?.detail || 'The cloud could not take your idea right now. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="start" className="relative min-h-screen px-6 sm:px-[8vw] py-[18vh]" data-testid="project-request-section">
      <AltitudeMarker index="06" label="PROJECT REQUEST" />
      <div className="relative max-w-5xl mx-auto">
        <div className="absolute -inset-x-[20%] -inset-y-[8%] pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(255,255,255,0.22), rgba(255,255,255,0) 65%)', filter: 'blur(40px)' }} />
        <p className="cloud-eyebrow mb-5">Altitude 06 — Your idea</p>
        <CloudText lines={['READY TO CREATE', 'SOMETHING?']} testId="request-title" />
        <Reveal delay={0.3}><p className="mt-6 text-base sm:text-lg text-white/75">Tell us what you're imagining.</p></Reveal>

        <AnimatePresence mode="wait">
          {done ? (
            <motion.div key="done" className="glass-deep relative mt-14 cloud-shape p-12 sm:p-20 text-center overflow-hidden" initial={{ scale: 0.6, opacity: 0, filter: 'blur(20px)' }} animate={{ scale: 1, opacity: 1, filter: 'blur(0px)' }} transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }} style={{ boxShadow: 'inset 0 0 90px 30px rgba(255,255,255,0.3), 0 40px 120px rgba(2,10,30,0.5)' }} data-testid="submission-success">
              {Array.from({ length: 8 }).map((_, i) => (
                <motion.span key={i} className="absolute rounded-full bg-white/70 blur-md" style={{ left: `${15 + i * 10}%`, top: '60%', width: 14 + (i % 3) * 8, height: 14 + (i % 3) * 8 }} animate={{ y: [-10, -160 - i * 20], opacity: [0.9, 0] }} transition={{ duration: 3 + i * 0.3, repeat: Infinity, delay: i * 0.25 }} />
              ))}
              <Cloud size={44} className="mx-auto text-white drop-shadow-[0_0_20px_rgba(255,255,255,0.8)]" />
              <h3 className="cloud-text text-3xl sm:text-5xl mt-6">YOUR IDEA IS<br />IN THE CLOUD.</h3>
              <p className="mt-5 text-base text-white/80">We’ll be in touch soon.</p>
            </motion.div>
          ) : (
            <motion.form key="form" onSubmit={submit} noValidate className="glass-deep relative mt-14 rounded-[2.5rem] p-6 sm:p-12 grid grid-cols-1 md:grid-cols-2 gap-6"
              exit={{ scale: 0.35, opacity: 0, filter: 'blur(24px)', borderRadius: '50%' }} transition={{ duration: 0.9, ease: 'easeInOut' }} data-testid="project-request-form">
              <Field label="Name" error={errors.name}><input className="glass-input" value={form.name} onChange={(e) => set('name')(e.target.value)} placeholder="Your name" data-testid="form-name-input" /></Field>
              <Field label="Email / Contact" error={errors.contact}><input className="glass-input" value={form.contact} onChange={(e) => set('contact')(e.target.value)} placeholder="Email, phone or @handle" data-testid="form-contact-input" /></Field>
              <Field label="Business or brand name"><input className="glass-input" value={form.business} onChange={(e) => set('business')(e.target.value)} placeholder="Optional" data-testid="form-business-input" /></Field>
              <Field label="What do you need?" error={errors.need}>
                <Select value={form.need} onValueChange={set('need')}>
                  <SelectTrigger className="glass-input h-auto py-[0.95rem] border-white/20 bg-white/5 text-left" data-testid="form-need-select"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                  <SelectContent className="glass-deep border-white/20 text-white rounded-2xl" data-testid="form-need-options">
                    {NEEDS.map((n) => <SelectItem key={n} value={n} className="focus:bg-white/15 focus:text-white rounded-xl" data-testid={`need-option-${n.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tell us about your project" error={errors.description} className="md:col-span-2">
                <textarea className="glass-input min-h-[160px] resize-y" value={form.description} onChange={(e) => set('description')(e.target.value)} placeholder="Describe your idea, what you want created, your style, colors, wording, dimensions, deadline and anything else we should know." data-testid="form-description-textarea" />
              </Field>
              <div className="md:col-span-2"><ReferenceUploader files={files} setFiles={setFiles} /></div>
              <Field label="Project budget" error={errors.budget}><BudgetSelector value={form.budget} onChange={set('budget')} /></Field>
              <Field label="Deadline">
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="glass-input flex items-center justify-between text-left" data-testid="form-deadline-button">
                      <span className={form.deadline ? '' : 'text-white/45'}>{form.deadline ? format(form.deadline, 'PPP') : 'Pick a date'}</span><CalendarIcon size={16} className="text-white/60" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="glass-deep border-white/20 text-white p-2 rounded-2xl w-auto" align="start">
                    <Calendar mode="single" selected={form.deadline} onSelect={set('deadline')} disabled={{ before: new Date() }} initialFocus data-testid="form-deadline-calendar" />
                  </PopoverContent>
                </Popover>
              </Field>
              <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-4">
                <p className="text-xs text-white/55 max-w-sm">Send your ideas, pictures, information, prices, colors and references all in ONE message so we can get straight to work.</p>
                <motion.button type="submit" disabled={sending} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="btn-cloud primary !px-9 !py-5 cloud-shape-2 !rounded-[40%_60%_55%_45%/55%_45%_55%_45%] text-sm" data-testid="form-submit-button">
                  {sending ? <motion.span animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}><Cloud size={16} /></motion.span> : <Send size={15} />}
                  {sending ? 'Lifting into the cloud…' : 'Send my project into the cloud'}
                </motion.button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

function Field({ label, error, children, className = '' }) {
  return (
    <div className={className}>
      <label className="field-label">{label}</label>
      {children}
      <AnimatePresence>{error && <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-2 text-xs text-[#ffb37a]" data-testid="form-error">{error}</motion.p>}</AnimatePresence>
    </div>
  );
}
